import React, { useState, useEffect, useRef, lazy, Suspense } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Dialog, DialogContent } from '../components/ui/dialog';
import { Play, ArrowRight, X, ChevronLeft, ChevronRight, Crown, Building2, Users } from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Avatar, AvatarFallback, AvatarImage } from '../components/ui/avatar';
import { api } from '../lib/api';
import ClaimBanner from '../components/landing/ClaimBanner';
import ServiceSplit from '../components/landing/ServiceSplit';
// Below-the-fold + on-demand components — lazy so they don't inflate the
// initial paint bundle. Landing visitors on Slow 4G shave hundreds of
// milliseconds because the Discover card grid + modals load only after
// scroll / user interaction. Webpack dedupes the three named-export
// dynamic imports into a single chunk.
const ClaimResendModal = lazy(() => import('../components/landing/ClaimResendModal'));
const WaitlistModal = lazy(() => import('../components/landing/WaitlistModal'));
const RadioStationCard = lazy(() =>
  import('../components/ui/DiscoverCards').then((m) => ({ default: m.RadioStationCard }))
);
const PlaylistCuratorCard = lazy(() =>
  import('../components/ui/DiscoverCards').then((m) => ({ default: m.PlaylistCuratorCard }))
);

// Avenue AR eyebrow — small uppercase red label + short red rule beneath.
const Eyebrow = ({ children, light = false }) => (
  <div>
    <span className={`text-xs font-medium uppercase tracking-[0.2em] text-avenue-red block`}>
      {children}
    </span>
    <span aria-hidden="true" className="block h-px bg-avenue-red mt-4 w-12" />
  </div>
);

// Horizontal Scroll Row Component — `light` flips text/controls for white sections.
const ScrollRow = ({ title, subtitle, light = false, children }) => {
  const scrollRef = useRef(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(true);

  const checkScroll = () => {
    if (scrollRef.current) {
      const { scrollLeft, scrollWidth, clientWidth } = scrollRef.current;
      setCanScrollLeft(scrollLeft > 0);
      setCanScrollRight(scrollLeft < scrollWidth - clientWidth - 10);
    }
  };

  useEffect(() => {
    checkScroll();
    const el = scrollRef.current;
    if (el) {
      el.addEventListener('scroll', checkScroll);
      return () => el.removeEventListener('scroll', checkScroll);
    }
  }, [children]);

  const scroll = (dir) => {
    if (scrollRef.current) {
      scrollRef.current.scrollBy({ left: dir === 'left' ? -400 : 400, behavior: 'smooth' });
    }
  };

  const ink = light ? 'text-avenue-black' : 'text-avenue-white';
  const inkMuted = light ? 'text-avenue-black/55' : 'text-avenue-white/50';
  const hairline = light ? 'bg-avenue-black/10 border-avenue-black/10' : 'bg-avenue-white/10 border-avenue-white/10';
  const btn = light
    ? 'bg-avenue-white text-avenue-black/60 hover:text-avenue-red'
    : 'bg-avenue-black text-avenue-white/60 hover:text-avenue-red';

  return (
    <div>
      <div className="flex items-end justify-between mb-5 px-6 md:px-10">
        <div>
          <h3 className={`font-editorial text-xl md:text-2xl ${ink} tracking-[-0.02em]`}>{title}</h3>
          {subtitle && <p className={`mt-1 text-sm ${inkMuted}`}>{subtitle}</p>}
        </div>
        <div className={`flex gap-px border ${hairline}`}>
          {canScrollLeft && (
            <button onClick={() => scroll('left')} aria-label="Scroll left" className={`w-9 h-9 flex items-center justify-center transition-colors duration-300 ${btn}`}>
              <ChevronLeft className="w-4 h-4" />
            </button>
          )}
          {canScrollRight && (
            <button onClick={() => scroll('right')} aria-label="Scroll right" className={`w-9 h-9 flex items-center justify-center transition-colors duration-300 ${btn}`}>
              <ChevronRight className="w-4 h-4" />
            </button>
          )}
        </div>
      </div>
      <div ref={scrollRef} className="flex gap-4 overflow-x-auto no-scrollbar px-6 md:px-10 pb-2" style={{ scrollSnapType: 'x mandatory' }}>
        {children}
      </div>
    </div>
  );
};

// Compact executive card — mirrors PlaylistCuratorCard (compact) 1:1 so the
// Featured Executives row reads identically to Trending Playlists.
const ExecMiniCard = ({ exec }) => {
  const name = exec.display_name || exec.name || 'Executive';
  const label = exec.company_name || exec.label || exec.role || '';
  const rawImage = exec.profile_image_url || exec.image;
  const fullImage =
    rawImage && rawImage.startsWith('/')
      ? `${process.env.REACT_APP_BACKEND_URL || ''}${rawImage}`
      : rawImage;
  // 200-px variant: avatar renders at 56 CSS px (h-14) = 112 px at 2x retina.
  const image =
    fullImage && fullImage.includes('/api/images/') && !/[?&]w=/.test(fullImage)
      ? `${fullImage}${fullImage.includes('?') ? '&' : '?'}w=200`
      : fullImage;
  const isOpen = exec.is_accepting_submissions;
  const isWaitlist = exec.claim_status === 'waiting_list' || exec.submission_status === 'waitlist';
  const genres = exec.genres || [];

  return (
    <Card className="transition-all duration-200 h-full hover:border-emerald-500/50">
      <CardContent className="p-4 h-full flex flex-col">
        <div className="flex items-start gap-4">
          <Avatar className="h-14 w-14 rounded-lg shrink-0">
            <AvatarImage src={image} alt={name} className="object-cover" />
            <AvatarFallback className="bg-avenue-red rounded-lg flex items-center justify-center text-white font-semibold text-lg">
              {name.charAt(0)}
            </AvatarFallback>
          </Avatar>
          <div className="flex-1 min-w-0">
            <h3 className="font-semibold truncate text-base">{name}</h3>
            <div className="flex items-center gap-3 text-sm text-muted-foreground flex-wrap">
              {label && (
                <span className="flex items-center gap-1 min-w-0">
                  <Building2 className="w-3 h-3 shrink-0" />
                  <span className="truncate" title={label}>{label}</span>
                </span>
              )}
              <span className="flex items-center gap-1">
                <Users className="w-3 h-3" />
                {isOpen ? 'Accepting' : isWaitlist ? 'Waitlist' : 'Executive'}
              </span>
            </div>
          </div>
        </div>

        {/* Genres - Pinned to bottom with mt-auto */}
        <div className="mt-auto pt-3">
          {genres.length > 0 ? (
            <div className="h-6 flex items-center gap-1 overflow-hidden">
              {genres.slice(0, 2).map((genre) => (
                <Badge key={genre} className="text-xs bg-emerald-500/10 text-emerald-400 border-emerald-500/20 whitespace-nowrap">
                  {genre}
                </Badge>
              ))}
            </div>
          ) : (
            <div className="h-6" />
          )}
        </div>
      </CardContent>
    </Card>
  );
};

export default function LandingPage() {
  const { isAuthenticated, user } = useAuth();
  const navigate = useNavigate();
  const [videoOpen, setVideoOpen] = useState(false);
  const [claimResendOpen, setClaimResendOpen] = useState(false);
  const [waitlistOpen, setWaitlistOpen] = useState(false);
  const [waitlistFeature, setWaitlistFeature] = useState('general');
  const openWaitlist = (feature) => { setWaitlistFeature(feature); setWaitlistOpen(true); };
  const [stats, setStats] = useState({
    artists: '...',
    executives: '...',
    radio_stations: '...',
    playlist_curators: '...',
    songs: '...',
    submissions: '...'
  });

  // Showcase data state - starts null to indicate loading
  const [showcaseData, setShowcaseData] = useState(null);

  // Rotating hero word — cycles "Heard → Discovered → Signed"
  const HERO_WORDS = ['Heard.', 'Discovered.', 'Signed.'];
  const [heroWordIndex, setHeroWordIndex] = useState(0);
  useEffect(() => {
    const id = setInterval(() => {
      setHeroWordIndex((i) => (i + 1) % HERO_WORDS.length);
    }, 2200);
    return () => clearInterval(id);
  }, []);

  useEffect(() => {
    const fetchStats = async () => {
      try {
        const data = await api.get('/auth/public/stats');
        setStats(data);
      } catch (err) {
        console.error('Failed to fetch stats:', err);
        setStats({
          artists: '10K+',
          executives: '500+',
          radio_stations: '300+',
          playlist_curators: '400+',
          songs: '50K+',
          submissions: '1K+'
        });
      }
    };

    const fetchShowcaseData = async () => {
      try {
        // Fetch executives from the PUBLIC endpoint (same data as Discover page)
        // Only get featured executives with profile images — we do NOT fall
        // back to mocks here per product decision. If no real executive has
        // an image yet, the Featured Executives row is simply hidden.
        const executivesResponse = await api.get('/executives/public?featured_only=true&with_images=true&limit=16');
        const executives = Array.isArray(executivesResponse) ? executivesResponse : [];

        // Always fetch the other showcase data (playlists + college radio).
        const data = await api.get('/auth/public/showcase');

        setShowcaseData({
          featuredExecutives: executives.slice(0, 8),
          topRadioStations: data.topRadioStations || [],
          trendingPlaylists: data.trendingPlaylists || [],
          risingExecutives: executives.slice(8, 16),
          collegeRadio: data.collegeRadio || [],
          newCurators: data.newCurators || [],
        });
      } catch (err) {
        console.error('Failed to fetch showcase data:', err);
        // On error, fall back to mock showcase data — but intentionally
        // blank out `featuredExecutives` so only real executives with
        // pictures are ever shown on the landing page.
        generateFallbackShowcaseData();
        setShowcaseData((prev) => (prev ? { ...prev, featuredExecutives: [], risingExecutives: [] } : prev));
      }
    };

    // Helper to generate avatar URL with colors
    const getAvatarUrl = (name, index) => {
      const colors = ['7B2AE2', '2A66C8', '22C55E', 'F59E0B', 'EF4444', '8B5CF6', '06B6D4', 'EC4899'];
      const color = colors[index % colors.length];
      const safeName = encodeURIComponent(name || 'Executive');
      return `https://ui-avatars.com/api/?name=${safeName}&background=${color}&color=fff&size=200&bold=true`;
    };

    // Generate fallback showcase data WITH profile images
    const generateFallbackShowcaseData = () => {
      const executiveNames = ['Marcus Thompson', 'Sarah Chen', 'David Rodriguez', 'Emily Watson', 'James Mitchell', 'Lisa Park', 'Michael Brown', 'Jennifer Lee'];
      const executiveRoles = ['A&R Director, Atlantic Records', 'VP A&R, Sony Music', 'Head of A&R, Interscope', 'Senior A&R, Republic Records', 'A&R Manager, RCA', 'Executive VP, Capitol Music', 'Director of Talent, Def Jam', 'A&R Coordinator, Warner Music'];

      const radioStations = [
        { name: 'KEXP 90.3', location: 'Seattle, WA', description: 'Championing independent music since 1972.' },
        { name: 'WFUV 90.7', location: 'New York, NY', description: 'Public radio featuring adult album alternative.' },
        { name: 'KCRW 89.9', location: 'Los Angeles, CA', description: 'Tastemaker station launching major artists.' },
        { name: 'WXPN 88.5', location: 'Philadelphia, PA', description: 'AAA format featuring World Cafe.' },
        { name: 'The Current', location: 'Minneapolis, MN', description: "Minnesota Public Radio's music discovery." },
        { name: 'KUTX 98.9', location: 'Austin, TX', description: "Austin's song station." },
        { name: 'WERS 88.9', location: 'Boston, MA', description: 'Emerson College radio since 1949.' },
        { name: 'KDHX 88.1', location: 'St. Louis, MO', description: 'Community radio with eclectic programming.' }
      ];

      const collegeStations = [
        { name: 'WHRB Harvard', location: 'Cambridge, MA', description: 'Harvard Radio Broadcasting.' },
        { name: 'WPRB Princeton', location: 'Princeton, NJ', description: "One of the nation's oldest college stations." },
        { name: 'KZSU Stanford', location: 'Stanford, CA', description: 'Freeform radio from the Bay Area.' },
        { name: 'WXYC UNC', location: 'Chapel Hill, NC', description: 'First station to stream on the internet.' },
        { name: 'WMBR MIT', location: 'Cambridge, MA', description: 'Late Risers Club programming.' },
        { name: 'KUCB Colorado', location: 'Boulder, CO', description: 'Radio 1190, award-winning college radio.' },
        { name: 'WRVU Vanderbilt', location: 'Nashville, TN', description: 'Nashville alternative in Music City.' },
        { name: 'KALX Berkeley', location: 'Berkeley, CA', description: 'UC Berkeley freeform radio.' }
      ];

      const curatorData = [
        { name: 'Fresh Finds', followers: '2.1M', genre: 'Indie & Alternative' },
        { name: 'Chill Vibes', followers: '1.8M', genre: 'Lo-Fi & Chill' },
        { name: 'Rap Caviar', followers: '14.2M', genre: 'Hip-Hop & Rap' },
        { name: 'Hot Country', followers: '6.5M', genre: 'Country' },
        { name: 'Rock This', followers: '4.3M', genre: 'Rock & Alternative' },
        { name: 'Peaceful Piano', followers: '7.1M', genre: 'Classical & Ambient' },
        { name: 'mint', followers: '5.8M', genre: 'Electronic & Dance' },
        { name: 'Are & Be', followers: '3.9M', genre: 'R&B & Soul' }
      ];

      const newCurators = [
        { name: 'Bedroom Pop', followers: '890K', genre: 'Indie Pop' },
        { name: 'Hyperpop', followers: '650K', genre: 'Experimental Pop' },
        { name: 'Jazz Vibes', followers: '1.2M', genre: 'Jazz & Neo-Soul' },
        { name: 'Punk Goes...', followers: '780K', genre: 'Punk & Emo' },
        { name: 'Latin Rising', followers: '2.3M', genre: 'Latin & Reggaeton' },
        { name: 'K-Pop Rising', followers: '3.1M', genre: 'K-Pop' },
        { name: 'African Heat', followers: '1.9M', genre: 'Afrobeats' },
        { name: 'Sad Songs', followers: '4.2M', genre: 'Emotional & Ballads' }
      ];

      // Build executives WITH profile images
      setShowcaseData({
        featuredExecutives: executiveNames.slice(0, 8).map((name, i) => ({
          name,
          display_name: name,
          role: executiveRoles[i],
          company_name: executiveRoles[i],
          profile_image_url: getAvatarUrl(name, i),
          image: getAvatarUrl(name, i),
          is_accepting_submissions: true
        })),
        topRadioStations: radioStations,
        trendingPlaylists: curatorData,
        risingExecutives: executiveNames.slice(0, 8).map((name, i) => ({
          name: name.split(' ').reverse().join(' '),
          display_name: name.split(' ').reverse().join(' '),
          role: executiveRoles[(i + 4) % 8],
          company_name: executiveRoles[(i + 4) % 8],
          profile_image_url: getAvatarUrl(name.split(' ').reverse().join(' '), i + 8),
          image: getAvatarUrl(name.split(' ').reverse().join(' '), i + 8),
          is_accepting_submissions: true
        })),
        collegeRadio: collegeStations,
        newCurators: newCurators
      });
    };

    fetchStats();
    fetchShowcaseData();
  }, []);

  const handleGetStarted = () => {
    if (isAuthenticated) {
      switch (user?.role) {
        case 'artist':
          navigate('/artist');
          break;
        case 'executive':
          navigate('/executive');
          break;
        case 'scout':
          navigate('/scout');
          break;
        case 'admin':
          navigate('/admin');
          break;
        default:
          navigate('/auth');
      }
    } else {
      navigate('/auth?tab=signup');
    }
  };

  // Artist / Executive / Hit List feature columns (content preserved).
  const FEATURE_COLUMNS = [
    {
      num: '01',
      title: 'For Artists',
      tagline: 'Get heard. Get signed.',
      pitch: null,
      ctaText: 'Start your artist journey',
      ctaTestId: 'for-artists-cta',
      items: [
        { text: 'Upload your song to request a professional review.' },
        { text: 'Submit to radio stations across the country for real airplay placement.', isNew: true },
        { text: 'Song Tickets — work directly with an A&R to develop a song they love.', coming: true, comingFeature: 'song_tickets' },
        { text: 'Hire A&R experts, music coaches, and digital managers for deeper strategy and development support.' },
        { text: 'Receive detailed song evaluations with clear, actionable feedback.' },
        { text: 'Schedule one-on-one video calls with executives to pitch your music.', coming: true, comingFeature: 'video_calls' },
        { text: 'Digital Scout — get matched with the executives actively searching for your sound.', coming: true, comingFeature: 'ai_scout' },
      ],
    },
    {
      num: '02',
      title: 'For Executives',
      tagline: 'Sign smarter. Move faster.',
      pitch: (
        <>
          <span className="text-avenue-red font-semibold">Reach the other 50%</span> — the independent artists your inbox, your scouts, and your label&apos;s SoundCloud crawl never see.
        </>
      ),
      ctaText: 'Become a featured executive',
      ctaTestId: 'for-executives-cta',
      ctaRole: 'executive',
      items: [
        { text: 'Discover new talent daily — fresh, vetted submissions delivered straight to your queue.' },
        { text: 'Work from desktop or phone — review, rate, and respond from anywhere in the world.' },
        { text: 'Complete CRM for you and your team — every artist conversation, decision, and deal in one place.' },
        { text: 'Track team results — see exactly which scouts and reps are sourcing the hits.' },
        { text: 'Every artist is ready to start working — full contact info right at your fingertips.' },
        { text: 'Monetize your account with the Pro Plan — earn revenue every time you decide.' },
        { text: 'Get paid contracts with artists and labels — close deals on platform with built‑in agreements.' },
      ],
    },
    {
      num: '03',
      title: 'The Hit List',
      tagline: 'Where the industry tunes in.',
      pitch: (
        <>
          The platform&apos;s <span className="text-avenue-red font-semibold">official chart of independent music</span> — climbed only by songs rated <span className="text-avenue-red font-semibold">7+ by real A&amp;R executives</span>. Every week, the entire AvenueAR Pro network opens it.
        </>
      ),
      ctaText: 'Climb the chart',
      ctaTestId: 'hit-list-cta',
      items: [
        { text: 'Songs rated 7 or higher are automatically added — no pay‑to‑play, no campaigns, just merit.' },
        { text: 'Instantly visible to every executive on the platform — your song lands in front of the people who sign deals.' },
        { text: 'Every radio station DJ gets a weekly Hit List newsletter — the chart is delivered straight to their inbox.' },
        { text: 'DJs and music directors stream the chart in‑app, line up favorites, and pull tracks straight into rotation.' },
        { text: 'Get spun on real radio — station rotations begin the moment a DJ taps "Add to Rotation" on your song.' },
        { text: 'Climb in real time — daily rank changes, plays, saves, and shares are all surfaced live on your artist page.' },
        { text: "Stay ahead of the curve — the Hit List is the earliest signal of what's about to break, weeks before it does." },
      ],
    },
  ];

  return (
    <div className="min-h-screen bg-avenue-black layout-bg-fill">
      {/* Sticky founding-member claim banner — top of page, dismissible */}
      <ClaimBanner onClaimClick={() => setClaimResendOpen(true)} />

      {/* Navigation */}
      <nav className="relative z-20 bg-avenue-black border-b border-avenue-white/10">
        <div className="mx-auto w-full max-w-avenue px-6 md:px-10 h-16 md:h-20 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <img src="/avenue-logo.png" alt="AvenueAR Pro" fetchPriority="high" decoding="async" className="h-9 sm:h-10 w-auto" />
            <span className="font-display text-sm sm:text-base text-avenue-red leading-none" aria-hidden="true">PRO</span>
          </Link>
          <div className="flex items-center gap-3 sm:gap-4">
            {/* Sign In is always available on the landing page so returning
                users (or users who want to switch accounts) can jump
                straight to the auth screen. */}
            <Link
              to="/auth"
              className="inline-flex items-center justify-center px-4 sm:px-5 py-2.5 text-xs font-medium uppercase tracking-wider text-avenue-white border border-avenue-white/40 hover:border-avenue-white transition-colors duration-300"
              data-testid="login-nav-btn"
            >
              Sign In
            </Link>
            {isAuthenticated ? (
              <button
                onClick={handleGetStarted}
                className="inline-flex items-center justify-center px-4 sm:px-6 py-2.5 text-xs font-medium uppercase tracking-wider text-avenue-white bg-avenue-red hover:bg-avenue-electric-red transition-colors duration-300"
                data-testid="dashboard-btn"
              >
                <span className="hidden sm:inline">Go to&nbsp;</span>Dashboard
              </button>
            ) : (
              <Link
                to="/auth?tab=signup"
                className="inline-flex items-center justify-center px-4 sm:px-6 py-2.5 text-xs font-medium uppercase tracking-wider text-avenue-white bg-avenue-red hover:bg-avenue-electric-red transition-colors duration-300"
                data-testid="signup-nav-btn"
              >
                <span className="sm:hidden">Start</span>
                <span className="hidden sm:inline">Get Started</span>
              </Link>
            )}
          </div>
        </div>
      </nav>

      {/* Hero Section — black, editorial stacked headline + image column */}
      <section className="w-full bg-avenue-black text-avenue-white relative overflow-hidden">
        <div className="mx-auto w-full px-6 md:px-10 max-w-avenue relative z-10">
          <div className="min-h-[72vh] grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-14 items-center pt-24 pb-16 md:pt-32 md:pb-24">
            <div className="lg:col-span-7">
              <Eyebrow>Independent Music, Taken Seriously</Eyebrow>
              <h1
                className="mt-8 md:mt-10 font-editorial text-avenue-white text-[clamp(2.5rem,5.4vw,4.95rem)]"
                style={{ lineHeight: 0.9 }}
              >
                Get
                <br />
                <span className="block relative h-[1em] overflow-hidden text-avenue-red">
                  {HERO_WORDS.map((word, i) => (
                    <span
                      key={word}
                      className={`absolute left-0 top-0 inline-block transition-all duration-500 ease-out ${
                        i === heroWordIndex
                          ? 'opacity-100 translate-y-0'
                          : i === (heroWordIndex - 1 + HERO_WORDS.length) % HERO_WORDS.length
                          ? 'opacity-0 -translate-y-full'
                          : 'opacity-0 translate-y-full'
                      }`}
                      aria-hidden={i !== heroWordIndex}
                    >
                      {word}
                    </span>
                  ))}
                  {/* invisible spacer so the line keeps its height */}
                  <span className="invisible">Discovered.</span>
                </span>
                By Real
                <br />
                Decision-
                <br />
                Makers.
              </h1>
              <p className="mt-8 max-w-xl text-base md:text-lg text-avenue-white/70 leading-relaxed">
                With AvenueAR Pro, your music is put in front of real decision&#8209;makers, not just algorithms — a direct line to A&amp;R reps, label execs, and music professionals actively looking for new talent. Every release is presented in a polished, professional way, so when the right person presses play, they see you as a serious artist from the start.
              </p>
              <p className="mt-5 max-w-xl text-base md:text-lg text-avenue-white/70 leading-relaxed">
                Instead of hoping to be noticed in a sea of uploads, you get a structured path to the people who can actually open doors&#8202;&mdash;&#8202;turning streams into real&#8209;world conversations, and conversations into life&#8209;changing connections.
              </p>
              <div className="mt-10 flex flex-col sm:flex-row sm:items-center gap-5">
                <button
                  onClick={() => setClaimResendOpen(true)}
                  className="group inline-flex items-center justify-center gap-2 font-medium uppercase tracking-wider bg-avenue-red text-avenue-white px-7 py-3.5 text-sm hover:bg-avenue-electric-red transition-colors duration-300"
                  data-testid="hero-claim-cta-btn"
                >
                  <Crown className="w-4 h-4" />
                  <span>Claim My Founding Account</span>
                </button>
                <button
                  onClick={handleGetStarted}
                  className="group inline-flex items-center justify-center gap-2 font-medium uppercase tracking-wider bg-transparent text-avenue-white px-0 py-1 text-sm hover:text-avenue-electric-red transition-colors duration-300"
                  data-testid="hero-cta-btn"
                >
                  <span>Start Your Journey</span>
                  <ArrowRight className="w-4 h-4 transition-transform duration-300 group-hover:translate-x-1" />
                </button>
                <button
                  onClick={() => setVideoOpen(true)}
                  className="group inline-flex items-center justify-center gap-2 font-medium uppercase tracking-wider bg-transparent text-avenue-white/60 px-0 py-1 text-sm hover:text-avenue-white transition-colors duration-300"
                  data-testid="hero-video-btn"
                >
                  <Play className="w-4 h-4" />
                  <span>Watch Video</span>
                </button>
              </div>
              <p className="mt-6 text-[11px] uppercase tracking-[0.22em] text-avenue-white/40 max-w-xl">
                Got the founding-member email? Click <span className="text-avenue-red">Claim My Founding Account</span> to retrieve your locked-rate access.
              </p>
            </div>
            <div className="lg:col-span-5">
              <div className="relative overflow-hidden bg-avenue-charcoal aspect-[4/5] lg:aspect-[3/4] w-full">
                <img
                  src="https://images.unsplash.com/photo-1598488035139-bdbb2231ce04?w=900&q=75&fm=webp&auto=format"
                  alt="Recording studio — where records get made and heard."
                  loading="lazy"
                  className="w-full h-full object-cover absolute inset-0 grayscale contrast-110"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-avenue-black/70 via-avenue-black/30 to-avenue-black/20" aria-hidden="true" />
              </div>
            </div>
          </div>

          {/* Stats strip — hairline grid, display numerals */}
          <div className="pb-16 md:pb-24">
            <div className="grid grid-cols-3 gap-px bg-avenue-white/10 border border-avenue-white/10">
              {[
                { label: 'Executives', value: stats.executives },
                { label: 'Radio Stations', value: stats.radio_stations },
                { label: 'Playlists', value: stats.playlist_curators },
              ].map((stat, i) => (
                <div key={i} className="bg-avenue-black p-5 md:p-7" data-testid={`landing-stat-${i}`}>
                  <span className="font-display text-[clamp(1.6rem,3.5vw,3rem)] leading-none text-avenue-red block">{stat.value}</span>
                  <span className="mt-3 block text-[11px] uppercase tracking-[0.22em] text-avenue-white/50">{stat.label}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Features Section — white, numbered editorial columns */}
      <section className="w-full bg-avenue-white text-avenue-black py-20 md:py-28">
        <div className="mx-auto w-full px-6 md:px-10 max-w-avenue">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16">
            <div className="lg:col-span-7">
              <Eyebrow light>The Platform</Eyebrow>
              <h2
                className="mt-7 md:mt-9 font-editorial text-avenue-black text-[clamp(2.25rem,4.8vw,4.25rem)]"
                style={{ lineHeight: 0.9 }}
              >
                How It
                <br />
                <span className="text-avenue-red">Works.</span>
              </h2>
            </div>
            <div className="lg:col-span-5 flex flex-col justify-end">
              <p className="text-base md:text-lg text-avenue-black/70 leading-relaxed">
                Simple, transparent, professional. One platform connecting artists, executives, radio stations, and curators — with real decisions on the other side.
              </p>
            </div>
          </div>

          <div className="mt-14 md:mt-20 grid grid-cols-1 md:grid-cols-3 gap-10 md:gap-12">
            {FEATURE_COLUMNS.map((col) => (
              <div key={col.num} className="border-t border-avenue-black/15 pt-6 flex flex-col h-full">
                <span className="font-display text-[clamp(3rem,6vw,4.5rem)] leading-none text-avenue-red block">{col.num}</span>
                <span className="mt-4 block font-editorial text-2xl md:text-3xl text-avenue-black tracking-[-0.02em]">{col.title}</span>
                <span className="mt-2 block text-[11px] uppercase tracking-[0.22em] text-avenue-black/50">{col.tagline}</span>
                {col.pitch && (
                  <p className="mt-4 text-base text-avenue-black/70 leading-relaxed">{col.pitch}</p>
                )}
                <ul className="mt-6 flex-1">
                  {col.items.map((item, i) => (
                    <li key={i} className="border-t border-avenue-black/10 py-3 flex items-start gap-4">
                      <span className="mt-2.5 h-px w-5 bg-avenue-red shrink-0" aria-hidden="true" />
                      <span className="flex-1 text-sm md:text-[15px] text-avenue-black/70 leading-relaxed">
                        {item.text}
                        {item.isNew && (
                          <span
                            className="ml-2 inline-flex items-center text-[10px] uppercase tracking-wider font-medium px-2 py-0.5 border border-avenue-red/40 text-avenue-red align-middle"
                            data-testid={`feature-new-pill-${i}`}
                          >
                            New
                          </span>
                        )}
                        {item.coming && (
                          ['song_tickets', 'ai_scout'].includes(item.comingFeature) ? (
                            <button
                              type="button"
                              onClick={() => openWaitlist(item.comingFeature || 'general')}
                              className="ml-2 inline-flex items-center text-[10px] uppercase tracking-wider font-medium px-2 py-0.5 border border-avenue-black/25 text-avenue-black/60 align-middle hover:border-avenue-red hover:text-avenue-red cursor-pointer transition-colors duration-300"
                              data-testid={`feature-soon-pill-${i}`}
                              title="Click to join the waitlist"
                            >
                              Coming Soon
                            </button>
                          ) : (
                            <span
                              className="ml-2 inline-flex items-center text-[10px] uppercase tracking-wider font-medium px-2 py-0.5 border border-avenue-red/40 text-avenue-red align-middle"
                              data-testid={`feature-soon-pill-${i}`}
                            >
                              New
                            </span>
                          )
                        )}
                      </span>
                    </li>
                  ))}
                </ul>
                <div className="mt-6 pt-5 border-t border-avenue-black/10">
                  <button
                    onClick={() => (col.ctaRole ? navigate(`/auth?role=${col.ctaRole}`) : handleGetStarted())}
                    className="group inline-flex items-center gap-2 text-xs uppercase tracking-[0.18em] text-avenue-red hover:text-avenue-electric-red transition-colors duration-300"
                    data-testid={col.ctaTestId}
                  >
                    {col.ctaText}
                    <ArrowRight className="w-4 h-4 transition-transform duration-300 group-hover:translate-x-1" />
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Showcase Rows — black, lazy-loaded (chunk pulls cards on demand) */}
      <Suspense fallback={<div className="py-16 bg-avenue-black" />}>
        <section className="w-full bg-avenue-black text-avenue-white py-20 md:py-28">
          <div className="mx-auto w-full max-w-avenue">
            <div className="px-6 md:px-10 mb-12 md:mb-16">
              <Eyebrow>The Network</Eyebrow>
              <h2
                className="mt-7 md:mt-9 font-editorial text-avenue-white text-[clamp(2.25rem,4.8vw,4.25rem)]"
                style={{ lineHeight: 0.9 }}
              >
                Discover
                <br />
                the
                <br />
                <span className="text-avenue-red">Industry.</span>
              </h2>
              <p className="mt-7 max-w-xl text-base md:text-lg text-avenue-white/70 leading-relaxed">
                Connect with top executives, radio stations, and playlist curators.
              </p>
            </div>

            {/* Hit Analyst / Demo Doctor split screen — replaces the large
                executive card row. Featured executives stay below playlists. */}
            <ServiceSplit onCta={handleGetStarted} />
          </div>
        </section>

        {/* Discover rows — Trending Playlists is the reference; College Radio
            and Featured Executives mirror it exactly (white section, light
            ScrollRow header, 280/320 card width, shadcn Card tiles). */}
        {showcaseData && (
          (showcaseData.trendingPlaylists?.length > 0 ||
            showcaseData.collegeRadio?.length > 0 ||
            showcaseData.featuredExecutives?.length > 0) && (
            <section className="w-full section-light bg-avenue-white py-16 md:py-20" data-testid="landing-discover-rows">
              <div className="mx-auto w-full max-w-avenue space-y-14 md:space-y-16">
                {/* Row 2: Trending Playlists (reference) */}
                {showcaseData.trendingPlaylists?.length > 0 && (
                  <div data-testid="landing-row-playlists">
                    <ScrollRow title="Trending Playlists" subtitle="High-impact playlist curators" light>
                      {showcaseData.trendingPlaylists.map((curator, i) => (
                        <div key={i} className="flex-shrink-0 w-[280px] sm:w-[320px]" style={{ scrollSnapAlign: 'start' }}>
                          <PlaylistCuratorCard curator={curator} showSubmitButton={false} compact={true} />
                        </div>
                      ))}
                    </ScrollRow>
                  </div>
                )}

                {/* Row 3: College Radio */}
                {showcaseData.collegeRadio?.length > 0 && (
                  <div data-testid="landing-row-radio">
                    <ScrollRow title="College Radio" subtitle="Influential campus stations" light>
                      {showcaseData.collegeRadio.map((station, i) => (
                        <div key={i} className="flex-shrink-0 w-[280px] sm:w-[320px]" style={{ scrollSnapAlign: 'start' }}>
                          <RadioStationCard station={station} showSubmitButton={false} compact={true} />
                        </div>
                      ))}
                    </ScrollRow>
                  </div>
                )}

                {/* Row 4: Featured Executives (compact) */}
                {showcaseData.featuredExecutives?.length > 0 && (
                  <div data-testid="landing-row-executives">
                    <ScrollRow title="Featured Executives" subtitle="Open to new submissions" light>
                      {showcaseData.featuredExecutives.slice(0, 12).map((exec, i) => (
                        <div key={exec.id || i} className="flex-shrink-0 w-[280px] sm:w-[320px]" style={{ scrollSnapAlign: 'start' }}>
                          <ExecMiniCard exec={exec} />
                        </div>
                      ))}
                    </ScrollRow>
                  </div>
                )}
              </div>
            </section>
          )
        )}
      </Suspense>

      {/* Final CTA Section — editorial, black */}
      <section className="w-full bg-avenue-black text-avenue-white py-24 md:py-32 border-t border-avenue-white/10">
        <div className="mx-auto w-full px-6 md:px-10 max-w-avenue">
          <div className="max-w-3xl">
            <Eyebrow>Join the Next Wave</Eyebrow>
            <h2
              className="mt-7 md:mt-9 font-editorial text-avenue-white text-[clamp(2.5rem,5.5vw,5rem)]"
              style={{ lineHeight: 0.9 }}
            >
              Ready to
              <br />
              Take Your
              <br />
              Career to
              <br />
              the Next
              <br />
              <span className="text-avenue-red">Level.</span>
            </h2>
            <p className="mt-8 max-w-xl text-base md:text-lg text-avenue-white/70 leading-relaxed">
              Create change — join the <span className="text-avenue-red font-semibold">[E]</span>volution.
            </p>
            <div className="mt-10 flex flex-col sm:flex-row sm:items-center gap-5">
              <button
                onClick={handleGetStarted}
                className="group inline-flex items-center justify-center gap-2 font-medium uppercase tracking-wider bg-avenue-red text-avenue-white px-7 py-3.5 text-sm hover:bg-avenue-electric-red transition-colors duration-300"
                data-testid="cta-btn"
              >
                <span>Get Started Free</span>
                <ArrowRight className="w-4 h-4 transition-transform duration-300 group-hover:translate-x-1" />
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="w-full bg-avenue-black border-t border-avenue-white/10 py-12">
        <div className="mx-auto w-full px-6 md:px-10 max-w-avenue flex flex-col md:flex-row items-center justify-between gap-6">
          <div className="flex items-center gap-2">
            <img src="/avenue-logo.png" alt="AvenueAR Pro" fetchPriority="high" decoding="async" className="h-8 w-auto" />
            <span className="font-display text-sm text-avenue-red leading-none" aria-hidden="true">PRO</span>
          </div>
          <nav className="flex items-center gap-6 text-xs uppercase tracking-[0.18em] text-avenue-white/60" data-testid="landing-legal-links">
            <Link to="/privacy" className="hover:text-avenue-red transition-colors duration-300" data-testid="footer-link-privacy">Privacy</Link>
            <Link to="/terms" className="hover:text-avenue-red transition-colors duration-300" data-testid="footer-link-terms">Terms</Link>
            <Link to="/fulfillment" className="hover:text-avenue-red transition-colors duration-300" data-testid="footer-link-fulfillment">Fulfillment</Link>
            <Link to="/help" className="hover:text-avenue-red transition-colors duration-300">Help</Link>
          </nav>
          <p className="text-[11px] uppercase tracking-[0.22em] text-avenue-white/40">
            © {new Date().getFullYear()} Neural Talk, LLC. All rights reserved.
          </p>
        </div>
      </footer>

      {/* Video Lightbox Modal */}
      <Dialog open={videoOpen} onOpenChange={setVideoOpen}>
        <DialogContent className="sm:max-w-4xl p-0 bg-avenue-black border-avenue-white/10 overflow-hidden">
          <button
            onClick={() => setVideoOpen(false)}
            className="absolute right-4 top-4 z-10 bg-avenue-black/60 p-2 text-avenue-white hover:bg-avenue-black/80 transition-colors duration-300"
          >
            <X className="w-5 h-5" />
          </button>
          <div className="aspect-video w-full">
            {videoOpen && (
              <iframe
                width="100%"
                height="100%"
                src="https://www.youtube.com/embed/pp9ANifduH0?autoplay=1&controls=0&rel=0&modestbranding=1&showinfo=0"
                title="AvenueAR Pro Demo"
                frameBorder="0"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                referrerPolicy="strict-origin-when-cross-origin"
                allowFullScreen
                className="w-full h-full"
              />
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Founding-member claim resend (typed-email recovery) — lazy modal */}
      <Suspense fallback={null}>
        <ClaimResendModal
          open={claimResendOpen}
          onClose={() => setClaimResendOpen(false)}
        />

        {/* Coming-soon waitlist (Video Calls / AI Scout pills) — lazy modal */}
        <WaitlistModal
          open={waitlistOpen}
          feature={waitlistFeature}
          onClose={() => setWaitlistOpen(false)}
        />
      </Suspense>
    </div>
  );
}
